import React, { FC } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import kehilaMigrationWizardStore from 'store/kehilaMigrationWizard.store'
import { RowSplit, KeyButton, Bar, BarFill, IdLine, Chip, Ledger, LedgerLine } from '../KehilaMigrationWizard.styles'

type TFn = (key: string, opts?: any) => string

const statusKind = (status: string): 'g' | 'r' | 'y' => {
  if (status === 'Success') return 'g'
  if (status === 'Failed') return 'r'
  return 'y'
}

const statusSign = (status: string): string => {
  if (status === 'Success') return '✓'
  if (status === 'Failed') return '!'
  return '='
}

const statusLabel = (status: string, t: TFn): string => {
  if (status === 'Success') return t('status_success')
  if (status === 'Failed') return t('status_failed')
  return t('status_skipped')
}

const ImportProtocolsStep: FC = () => {
  const store = kehilaMigrationWizardStore
  const { t } = useTranslation('kehilaMigration')
  const status = store.importStatus
  const pct = status && status.total > 0 ? Math.round((status.done / status.total) * 100) : 0
  const finished = !!status && status.done >= status.total && !status.isRunning
  const buttonLabel = !status ? t('import_start') : finished ? t('import_complete') : (status.isRunning ? t('import_running') : t('import_resume'))

  return (
    <>
      <RowSplit>
        <KeyButton disabled={finished || store.starting || Boolean(status?.isRunning)} onClick={store.startImport}>
          {buttonLabel}
        </KeyButton>
      </RowSplit>
      <Bar role='progressbar' aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <BarFill pct={pct} />
      </Bar>
      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0 16px' }}>
        <IdLine>{status ? `${status.done} / ${status.total}` : '0 / 0'}</IdLine>
        <IdLine>{pct}%</IdLine>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <Chip kind='ok'>{t`import_imported`} {status?.imported ?? 0}</Chip>
        <Chip>{t`import_skipped`} {status?.skipped ?? 0}</Chip>
        <Chip kind={status?.failed ? 'bad' : undefined}>{t`import_failed`} {status?.failed ?? 0}</Chip>
      </div>
      <Ledger aria-live='polite' aria-label={t`import_logLabel`}>
        {(status?.recentEntries ?? []).filter(e => e.protocolOldId != null).slice(0, 8).reverse().map(entry => (
          <LedgerLine key={entry.id} kind={statusKind(entry.status)}>
            {`${statusSign(entry.status)} ${entry.protocolOldId ?? ''} ${statusLabel(entry.status, t)}${entry.statusDetails ? ' · ' + entry.statusDetails : ''}`}
          </LedgerLine>
        ))}
      </Ledger>
    </>
  )
}

export default observer(ImportProtocolsStep)
